/**
 * Shared form-hardening utilities for public registration / lead-gen forms.
 *
 * Layers (defense-in-depth):
 *  1. zod schemas with tight length + pattern rules.
 *  2. Disposable-email blocklist (small, common offenders; not exhaustive).
 *  3. Client-side rate limiter via localStorage (best-effort — RLS / edge
 *     functions are the real backstop; this just blunts casual abuse).
 *  4. Honeypot + min-fill-time helpers (already used in components).
 *
 * NOTE: All of this is client-side. Never treat it as a security boundary —
 * always pair with server-side validation (zod on edge functions, RLS,
 * unique constraints). This layer reduces noise, not threat surface.
 */
import { z } from "zod";

// Common throwaway providers. Keep this list short — false positives cost
// real signups. Add more only with evidence of abuse.
const DISPOSABLE_DOMAINS = new Set([
  "mailinator.com",
  "guerrillamail.com",
  "tempmail.com",
  "10minutemail.com",
  "yopmail.com",
  "throwawaymail.com",
  "trashmail.com",
  "fakeinbox.com",
  "getnada.com",
  "sharklasers.com",
  "maildrop.cc",
  "discard.email",
  "mintemail.com",
  "spam4.me",
]);

const NAME_PATTERN = /^[\p{L}\p{M}'’\-.\s]{2,120}$/u;
// Tighter than the default zod email — requires a real TLD and no spaces.
const EMAIL_PATTERN = /^[a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,}$/;

export const nameField = z
  .string()
  .trim()
  .min(2, "Name required")
  .max(120, "Name too long")
  .regex(NAME_PATTERN, "Use letters, spaces, hyphens or apostrophes only");

export const emailField = z
  .string()
  .trim()
  .toLowerCase()
  .min(5, "Email required")
  .max(255, "Email too long")
  .regex(EMAIL_PATTERN, "Enter a valid email address")
  .refine((v) => {
    const domain = v.split("@")[1];
    return !!domain && !DISPOSABLE_DOMAINS.has(domain);
  }, "Please use a real, permanent email address");

export const registrationSchema = z.object({
  full_name: nameField,
  email: emailField,
});

export type RegistrationInput = z.infer<typeof registrationSchema>;

// --- Client-side rate limiter ------------------------------------------
const RL_KEY = "drsully:form-rl:v1";
type RLBucket = { count: number; firstAt: number };

type RateLimitOpts = {
  /** logical bucket name, e.g. "lead-magnet" */
  bucket: string;
  /** max attempts per window */
  max?: number;
  /** rolling window in ms */
  windowMs?: number;
};

export type RateLimitResult = {
  ok: boolean;
  remaining: number;
  retryAfterMs: number;
};

export function checkRateLimit({
  bucket,
  max = 5,
  windowMs = 60 * 60 * 1000,
}: RateLimitOpts): RateLimitResult {
  if (typeof window === "undefined") return { ok: true, remaining: max, retryAfterMs: 0 };
  let store: Record<string, RLBucket> = {};
  try {
    store = JSON.parse(window.localStorage.getItem(RL_KEY) ?? "{}");
  } catch {
    store = {};
  }
  const now = Date.now();
  const b = store[bucket];
  if (!b || now - b.firstAt > windowMs) {
    store[bucket] = { count: 1, firstAt: now };
    safeWrite(store);
    return { ok: true, remaining: max - 1, retryAfterMs: 0 };
  }
  if (b.count >= max) {
    return { ok: false, remaining: 0, retryAfterMs: b.firstAt + windowMs - now };
  }
  b.count += 1;
  safeWrite(store);
  return { ok: true, remaining: max - b.count, retryAfterMs: 0 };
}

function safeWrite(store: Record<string, RLBucket>) {
  try {
    window.localStorage.setItem(RL_KEY, JSON.stringify(store));
  } catch {
    /* private mode / quota — ignore */
  }
}

export function formatRetryAfter(ms: number): string {
  const mins = Math.ceil(ms / 60000);
  if (mins < 60) return `${mins} minute${mins === 1 ? "" : "s"}`;
  const hrs = Math.ceil(mins / 60);
  return `${hrs} hour${hrs === 1 ? "" : "s"}`;
}

// --- Honeypot / bot-signal helpers --------------------------------------
/**
 * Field names rendered off-screen. Real users never fill them; naive bots
 * that auto-complete every input in the DOM will.
 */
export const HONEYPOT_FIELDS = ["website", "company_url", "confirm_email"] as const;
/** Hidden checkbox bots love to tick. */
export const HONEYPOT_CHECKBOX = "subscribe_offers";

export type BotSignalOpts = {
  formData: FormData;
  /** Date.now() captured when the form mounted. */
  startedAt: number;
  /** True once the user has produced a real pointer/key event on the form. */
  interacted: boolean;
  /** Minimum plausible fill time. */
  minFillMs?: number;
};

export type BotSignalResult =
  /** Silently drop — near-certain bot. Do not show an error. */
  | { verdict: "drop"; reason: string }
  /** Ask the human to slow down / try again. */
  | { verdict: "challenge"; reason: string; message: string }
  | { verdict: "pass" };

export function evaluateBotSignals({
  formData,
  startedAt,
  interacted,
  minFillMs = 2500,
}: BotSignalOpts): BotSignalResult {
  for (const name of HONEYPOT_FIELDS) {
    const v = formData.get(name);
    if (typeof v === "string" && v.trim().length > 0) {
      return { verdict: "drop", reason: `honeypot:${name}` };
    }
  }
  if (formData.get(HONEYPOT_CHECKBOX) != null) {
    return { verdict: "drop", reason: `honeypot:${HONEYPOT_CHECKBOX}` };
  }
  if (!interacted) {
    return { verdict: "drop", reason: "no-human-interaction" };
  }
  if (Date.now() - startedAt < minFillMs) {
    return {
      verdict: "challenge",
      reason: "too-fast",
      message: "Please take a moment to complete the form.",
    };
  }
  return { verdict: "pass" };
}
